/**
 * FontTransferablePreparer - Utility for preparing font bytes as Transferable objects
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility handles the critical optimization of transferring font bytes to Web Workers
 * using Transferable objects instead of JSON serialization. This prevents JSON corruption
 * when large font byte arrays are serialized, and improves performance by transferring
 * ownership rather than copying data.
 */

/**
 * Type definition for font byte index tracking
 */
export interface FontByteIndex {
  paraIndex: number;
  fontIndex: number;
  bufferIndex: number;
  fontUid: string;
  bytesLength: number;
}

/**
 * Result of preparing fonts for transfer
 */
export interface FontTransferableResult {
  inputWithoutBytes: any; // Input with font bytes removed
  fontByteIndices: FontByteIndex[];
  fontBuffers: ArrayBuffer[];
  totalBytesTransferred: number;
}

/**
 * Prepare font bytes as Transferable objects for Web Worker communication
 * 
 * This function:
 * 1. Creates a deep copy of the input without font bytes (to avoid JSON corruption)
 * 2. Extracts font bytes as ArrayBuffers for transfer
 * 3. Tracks font positions via indices for reconstruction in the worker
 * 4. Marks fonts in the input copy for reconstruction
 * 
 * @param input - Document layout input containing paragraphs with fonts
 * @returns Prepared data structure with Transferable ArrayBuffers and tracking indices
 */
export function prepareFontsAsTransferable(input: any): FontTransferableResult {
  const transferables: ArrayBuffer[] = [];
  const fontByteIndices: FontByteIndex[] = [];
  
  // Create a deep copy of input without font bytes (to avoid JSON corruption)
  // We'll send ArrayBuffers separately as Transferable objects
  const inputWithoutBytes = JSON.parse(JSON.stringify(input, (key, value) => {
    // Remove font bytes from JSON serialization
    if (key === 'bytes' && value instanceof Uint8Array) {
      return null; // Remove from JSON
    }
    return value;
  }));
  
  // Extract font bytes as ArrayBuffers for transfer and track their positions
  if (input.paragraphs) {
    input.paragraphs.forEach((para: any, paraIndex: number) => {
      if (para.fonts) {
        para.fonts.forEach((font: any, fontIndex: number) => {
          if (font.bytes instanceof Uint8Array && font.bytes.length > 0) {
            // Create a copy of the ArrayBuffer for transfer (original will be detached)
            const buffer = font.bytes.buffer.slice(
              font.bytes.byteOffset,
              font.bytes.byteOffset + font.bytes.byteLength
            );
            transferables.push(buffer);
            const bufferIndex = transferables.length - 1;
            fontByteIndices.push({ 
              paraIndex, 
              fontIndex, 
              bufferIndex,
              fontUid: font.font_uid,
              bytesLength: font.bytes.length
            });
            
            // Mark in the input copy that this font needs reconstruction
            if (inputWithoutBytes.paragraphs[paraIndex]?.fonts[fontIndex]) {
              inputWithoutBytes.paragraphs[paraIndex].fonts[fontIndex].bytes_transferable = true;
              inputWithoutBytes.paragraphs[paraIndex].fonts[fontIndex].bytes_length = font.bytes.length;
            }
          }
        });
      }
    });
  }
  
  // Calculate total bytes for logging
  const totalBytesTransferred = transferables.reduce((sum, buf) => sum + buf.byteLength, 0);
  
  return {
    inputWithoutBytes,
    fontByteIndices,
    fontBuffers: transferables,
    totalBytesTransferred
  };
}

/**
 * Create a message payload with Transferable font buffers
 * 
 * @param requestId - Unique request identifier
 * @param inputWithoutBytes - Input with font bytes removed
 * @param fontByteIndices - Tracking indices for font reconstruction
 * @param fontBuffers - ArrayBuffers to be transferred
 * @returns Message payload ready for postMessage
 */
export function createTransferableMessagePayload(
  requestId: string,
  inputWithoutBytes: any,
  fontByteIndices: FontByteIndex[],
  fontBuffers: ArrayBuffer[]
): { type: string; payload: any } {
  return {
    type: 'layout-paginated',
    payload: { 
      id: requestId, 
      input: inputWithoutBytes, // Input without font bytes (to avoid JSON corruption)
      fontByteIndices, // Map to reconstruct fonts from Transferable ArrayBuffers
      fontBuffers // ArrayBuffers to be transferred
    }
  };
}

